window.CategoryPage = function CategoryPage({ 
  categoryData,
  setActiveTab,
  setSelectedProductSlug,
  openQuoteModal
}) {
  const [allProducts, setAllProducts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    fetchProducts();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await window.apiClient.get('products.php');
      if (res.success && res.data && res.data.length > 0) {
        setAllProducts(res.data);
      }
    } catch (e) {
      console.warn('Products fetch fallback:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!categoryData) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center p-8 bg-slate-50">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Category Not Found</h2>
        <button 
          onClick={() => setActiveTab('industries')}
          className="bg-secondary hover:bg-orange-600 text-white px-6 py-2 rounded-xl transition-colors font-bold"
        >
          Return to Industries
        </button>
      </div>
    );
  }

  const categoryProducts = allProducts.filter(p => 
    categoryData.productIds && categoryData.productIds.includes(String(p.id))
  );

  return (
    <div className="bg-slate-50 min-h-screen py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        
        {/* Page Header */}
        <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 relative overflow-hidden shadow-2xl">
          <div className="max-w-3xl space-y-4 relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <button 
                onClick={() => setActiveTab('industries')}
                className="text-slate-400 hover:text-white transition-colors flex items-center text-xs font-bold uppercase tracking-widest"
              >
                <span className="material-symbols-outlined text-sm mr-1">arrow_back</span>
                {categoryData.industryTitle || 'Industries'}
              </button>
            </div>
            
            <h1 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
              {categoryData.title}
            </h1>
            
            {categoryData.desc && (
              <p className="font-sans text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                {categoryData.desc}
              </p>
            )}
            
            <div className="pt-4 flex flex-wrap gap-4">
              <button
                onClick={() => setActiveTab('products')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 font-sans font-bold text-sm px-6 py-3 rounded-xl transition-all flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">inventory_2</span>
                <span>View All Products</span>
              </button>
              <button
                onClick={openQuoteModal}
                className="bg-secondary hover:bg-orange-600 text-white font-sans font-bold text-sm px-6 py-3 rounded-xl transition-all shadow-lg shadow-orange-500/20 flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">engineering</span>
                <span>Request Consultation</span>
              </button>
            </div>
          </div>
          
          <div className="absolute right-0 bottom-0 opacity-10 pointer-events-none">
            <span className="material-symbols-outlined text-[250px]">category</span>
          </div>
        </div>

        {/* Products Grid */}
        <div className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h2 className="font-sans text-2xl font-bold text-slate-900">
                Dedicated Systems
              </h2>
              <p className="text-slate-500 text-sm mt-1">
                Found {categoryProducts.length} product{categoryProducts.length !== 1 ? 's' : ''} for this category.
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-20 text-center">
              <span className="material-symbols-outlined text-4xl text-slate-300 animate-spin">refresh</span>
              <p className="text-slate-500 text-sm mt-4 font-mono">Loading products...</p>
            </div>
          ) : categoryProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {categoryProducts.map(prod => (
                <div 
                  key={prod.id} 
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer group flex flex-col h-full"
                  onClick={() => {
                    if (setSelectedProductSlug) setSelectedProductSlug(prod.slug || prod.id);
                    setActiveTab('product-detail');
                  }}
                >
                  <div className="h-48 bg-slate-100 relative overflow-hidden">
                    <img 
                      src={window.resolveMediaUrl(prod.primary_image || prod.image, 'product')} 
                      alt={prod.name}
                      onError={(e) => window.handleImageError(e, 'product')}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-secondary text-[10px] font-sans font-bold px-2.5 py-1 rounded-md shadow-sm">
                      {prod.category || 'Product'}
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-grow">
                    <h3 className="font-sans text-lg font-bold text-slate-900 group-hover:text-secondary transition-colors mb-2 line-clamp-2">
                      {prod.name}
                    </h3>
                    <p className="font-sans text-sm text-slate-600 line-clamp-3 mb-4 flex-grow">
                      {prod.short_description || prod.description}
                    </p>
                    <div className="flex items-center text-secondary font-bold text-xs mt-auto pt-4 border-t border-slate-100">
                      <span>View Specifications</span>
                      <span className="material-symbols-outlined text-sm ml-auto group-hover:translate-x-1 transition-transform">arrow_forward</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center border-2 border-dashed border-slate-200 rounded-3xl bg-white">
              <span className="material-symbols-outlined text-5xl text-slate-300 mb-3">inventory_2</span>
              <h3 className="text-lg font-bold text-slate-700 mb-1">No Products Found</h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">
                There are currently no products assigned to this category.
              </p>
              <button
                onClick={() => setActiveTab('products')}
                className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-xl text-sm font-bold transition-colors"
              >
                Browse All Products
              </button>
            </div>
          )}
        </div>
        
      </div>
    </div>
  );
};
